import { useAnalytics } from "@hooks/useAnalytics";
import { useRichText } from "@hooks/useRichText";
import {
  ComponentProps,
  FromSanityGraphQL,
  ResolvedLink,
  WithGatsbyImage,
} from "@ts/components";
import {
  CarouselSection,
  CustomImage,
  FeaturedCollection,
  SanityKeyed,
  SectionFeaturedCollection,
} from "@ts/sanity";
import React, { FC, useCallback, useEffect, useState } from "react";

export type FeaturedCollectionFromQuery = FeaturedCollection & {
  collectionContents: Array<SanityKeyed<CarouselSection>>;
};

type TabTitleProps = {
  title?: string;
};

export type FeaturedCollectionContent = SanityKeyed<
  WithGatsbyImage<
    {
      image: SanityKeyed<CustomImage>;
      link: ResolvedLink;
    },
    "image"
  >
>;

type FeaturedCollectionInputProps = ComponentProps &
  FromSanityGraphQL<SectionFeaturedCollection> & {
    _key: string;
    featuredCollectionContents: FeaturedCollectionFromQuery[];
  };

type FeaturedCollectionOutputProps = Omit<
  FeaturedCollectionInputProps,
  "collectionContents"
> & {
  contents: FeaturedCollectionContent[];
  tabTitle?: TabTitleProps[];
  handlePromoClick: (title?: string) => void;
  handleTabClick: (title?: string) => void;
  activeTab?: string;
};

export type FeaturedCollectionCardProps = Pick<
  FeaturedCollectionOutputProps,
  "handlePromoClick"
> &
  Omit<FeaturedCollectionContent, "_key">;

export const withFeaturedCollection =
  (Component: FC<FeaturedCollectionOutputProps>) =>
  ({
    name = "FeaturedCollection",
    analytics,
    featuredCollections,
    featuredCollectionContents,
    heading,
    location,
    _key,
    ...rest
  }: FeaturedCollectionInputProps) => {
    const { parseContent } = useRichText();
    const { trackPromoImpression, trackPromoClick, VisibilitySensor } =
      useAnalytics();
    const normalisedHeading = parseContent(heading);
    const normalisedFeaturedCollections =
      featuredCollections ?? featuredCollectionContents;

    const [tabTitle, setTabTitle] = useState<TabTitleProps[]>();
    const [activeTab, setActiveTab] = useState<string | undefined>("");

    const handleTabClick = useCallback(
      (title?: string) => {
        setActiveTab(title);

        if (!analytics || !title) {
          return;
        }

        trackPromoImpression({
          ...analytics,
          analyticsId: `${analytics.analyticsId} - ${title}`,
          name: `${analytics.analyticsId} - ${title}`,
        });
      },
      [analytics, trackPromoImpression],
    );

    const handlePromoClick = useCallback(
      (title?: string) => {
        if (!analytics) {
          return;
        }

        trackPromoClick({
          ...analytics,
          analyticsId: !activeTab
            ? `${analytics.analyticsId} - ${title}`
            : `${analytics.analyticsId} - ${activeTab} - ${title}`,
          name: !activeTab
            ? `${analytics.analyticsId} - ${title}`
            : `${analytics.analyticsId} - ${activeTab} - ${title}`,
        });
      },
      [analytics, trackPromoClick, activeTab],
    );

    const handleVisibilityChange = useCallback(
      (visible: boolean) => {
        if (!visible || !analytics) {
          return;
        }

        trackPromoImpression(analytics);
      },
      [analytics, trackPromoImpression],
    );

    useEffect(() => {
      if (normalisedFeaturedCollections?.length > 1) {
        const title = normalisedFeaturedCollections?.map((content) => ({
          title: content.title,
        }));
        setTabTitle(title);
        setActiveTab(title?.[0]?.title);
      }
    }, [normalisedFeaturedCollections]);

    Component.displayName = name;
    return (
      <VisibilitySensor onChange={handleVisibilityChange}>
        <Component
          _key={_key}
          activeTab={activeTab}
          featuredCollectionContents={normalisedFeaturedCollections}
          handlePromoClick={handlePromoClick}
          handleTabClick={handleTabClick}
          heading={normalisedHeading}
          tabTitle={tabTitle}
          {...rest}
        />
      </VisibilitySensor>
    );
  };
